export interface OperationsHealth {
  ok: boolean
  schemaVersion: number
  projectCount: number
  jobCount: number
  activeJobCount: number
}

export interface OperationsDiagnostics {
  generatedAt: string
  runtime: {
    nodeVersion: string
    platform: string
    osRelease: string
    cwd: string
  }
  operations: OperationsHealth
  codex: {
    installed: boolean
    codexPath: string | null
    codexCliVersion: string | null
    appServer: {
      command: string
      telemetryRead: string
      reason: string
    }
  }
  bridge: {
    version: string
    installed: boolean
    paired: boolean
    configured: boolean
    deviceId: string | null
    paths: {
      root: string
      configPath: string
      credentialPath: string
      logPath: string
    }
  }
}

export interface OperationsJob {
  id: string
  normalizedObjective: string
  state: string
  mode: string
  provider: string
  approvalState: string
  artifacts?: Array<{
    id: string
    type: string
    label: string
    relativePath: string
    sha256: string
  }>
  updatedAt: string
  completedAt: string | null
}

export interface OperationsArtifactContent {
  artifact: {
    id: string
    jobId: string
    artifactType: string
    label: string
    relativePath: string
    sha256: string
    sizeBytes: number
    contentType: string
    createdAt: string
  }
  content: unknown
}

export interface OperationsArtifactSummary {
  id: string
  jobId: string
  artifactType: string
  label: string
  relativePath: string
  sha256: string
  sizeBytes: number
  contentType: string
  createdAt: string
  job: {
    id: string
    projectId: string | null
    normalizedObjective: string
    state: string
    updatedAt: string
  }
}

export interface OperationsCommandPreset {
  id: string
  label: string
  description: string
  riskLevel: string
}

export interface OperationsProject {
  id: string
  displayName: string
  repositoryPath: string
  defaultCommands: Record<string, {
    id: string
    label: string
    description: string
    command: string
    args: string[]
    riskLevel: string
    requiresApproval: boolean
  }>
}

export interface OperationsProjectCommand {
  id: string
  label: string
  description: string
  riskLevel: string
  requiresApproval: boolean
}

export interface OperationsApproval {
  id: string
  jobId: string | null
  riskLevel: string
  requestedAction: string
  status: string
  rationale: string
  requestedAt: string
}

export interface OperationsTask {
  id: string
  source: 'approval' | 'release-gate' | string
  jobId?: string | null
  riskLevel: string
  requestedAction: string
  rationale: string
  status: string
  requestedAt: string
  action?: string
  actionLabel?: string
  details?: Record<string, string | null | undefined>
}

export interface OperationsOverview {
  health: OperationsHealth
  diagnostics: OperationsDiagnostics
  projects: OperationsProject[]
  projectCommandsByProjectId: Record<string, OperationsProjectCommand[]>
  jobs: OperationsJob[]
  artifacts: OperationsArtifactSummary[]
  approvals: OperationsApproval[]
  tasks: OperationsTask[]
  commands: OperationsCommandPreset[]
}

declare global {
  interface Window {
    TREVAHHH_OPS_API_URL?: string
  }
}

function configuredBaseUrl(): string {
  if (typeof window !== 'undefined' && window.TREVAHHH_OPS_API_URL) return window.TREVAHHH_OPS_API_URL.replace(/\/+$/, '')
  return 'http://127.0.0.1:8787'
}

export class OperationsClient {
  constructor(private baseUrl = configuredBaseUrl(), private timeoutMs = 1200) {}

  async readOverview(): Promise<OperationsOverview> {
    const [health, diagnostics, projectsResponse, jobsResponse, artifactsResponse, approvalsResponse, tasksResponse, commandsResponse] = await Promise.all([
      this.readJson<OperationsHealth>('/api/health'),
      this.readJson<OperationsDiagnostics>('/api/diagnostics'),
      this.readJson<{ projects: OperationsProject[] }>('/api/projects'),
      this.readJson<{ jobs: OperationsJob[] }>('/api/jobs'),
      this.readJson<{ artifacts: OperationsArtifactSummary[] }>('/api/artifacts?limit=25'),
      this.readJson<{ approvals: OperationsApproval[] }>('/api/approvals?status=pending'),
      this.readJson<{ tasks: OperationsTask[] }>('/api/tasks').catch(() => ({ tasks: [] })),
      this.readJson<{ commands: OperationsCommandPreset[] }>('/api/commands')
    ])
    const projects = projectsResponse.projects.slice(0, 5)
    const projectCommandEntries = await Promise.all(projects.map(async (project) => {
      const response = await this.readJson<{ commands: OperationsProjectCommand[] }>(`/api/projects/${encodeURIComponent(project.id)}/commands`)
      return [project.id, response.commands] as const
    }))
    return {
      health,
      diagnostics,
      projects,
      projectCommandsByProjectId: Object.fromEntries(projectCommandEntries),
      jobs: jobsResponse.jobs.slice(0, 5),
      artifacts: artifactsResponse.artifacts.slice(0, 25),
      approvals: approvalsResponse.approvals.slice(0, 5),
      tasks: tasksResponse.tasks.slice(0, 5),
      commands: commandsResponse.commands
    }
  }

  async startPreset(presetId: string): Promise<OperationsJob> {
    const result = await this.readJson<{ job: OperationsJob }>('/api/jobs/start-preset', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ presetId })
    })
    return result.job
  }

  async cancelJob(id: string): Promise<OperationsJob> {
    const result = await this.readJson<{ job: OperationsJob }>(`/api/jobs/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ reason: 'Cancellation requested from Trevahhh UI' })
    })
    return result.job
  }

  async respondApproval(id: string, response: 'approved' | 'rejected', responder = 'Jay'): Promise<OperationsApproval> {
    const result = await this.readJson<{ approval: OperationsApproval }>(`/api/approvals/${encodeURIComponent(id)}/respond`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ response, responder })
    })
    return result.approval
  }

  async completeJayTask(id: string, status: 'completed' | 'dismissed' = 'completed', responder = 'Jay'): Promise<OperationsTask> {
    const result = await this.readJson<{ task: OperationsTask }>(`/api/tasks/${encodeURIComponent(id)}/complete`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ status, responder })
    })
    return result.task
  }

  async readArtifactContent(jobId: string, artifactId: string): Promise<OperationsArtifactContent> {
    return await this.readJson<OperationsArtifactContent>(`/api/jobs/${encodeURIComponent(jobId)}/artifacts/${encodeURIComponent(artifactId)}/content`)
  }

  async updateProjectCommands(projectId: string, commands: unknown): Promise<{ project: OperationsProject; commands: OperationsProjectCommand[] }> {
    return await this.readJson<{ project: OperationsProject; commands: OperationsProjectCommand[] }>(`/api/projects/${encodeURIComponent(projectId)}/commands`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ commands })
    })
  }

  async startProjectCommand(projectId: string, commandId: string): Promise<OperationsJob> {
    const result = await this.readJson<{ job: OperationsJob }>(`/api/projects/${encodeURIComponent(projectId)}/commands/${encodeURIComponent(commandId)}/start`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: '{}'
    })
    return result.job
  }

  private async readJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), this.timeoutMs)
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
        headers: { accept: 'application/json' },
        ...init
      })
      if (!response.ok) throw new Error(`Operations API returned ${response.status}`)
      return await response.json() as T
    } finally {
      window.clearTimeout(timer)
    }
  }
}

export function createOperationsClient(): OperationsClient {
  return new OperationsClient()
}
